/**
 * Ryan O'Dowd
 * 2022-05-15
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import AnimatedRings from './AnimatedRings';
import Badge from '@components/common/Badge';
import React from 'react';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const ProgressIndicator = (_p: ProgressIndicatorProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const styles = hooks.useStyles(getStyles);

  let masteredFillPercent = props.completedNum / props.totalNum * 100;
  let inProgressFillPercent = masteredFillPercent + (props.inProgressNum / props.totalNum * 100);
  let unstartedFillPercent = inProgressFillPercent + ((props.unstartedNum || 0) / props.totalNum * 100);

  if (isNaN(masteredFillPercent)) {
    masteredFillPercent = 0;
  }
  if (isNaN(inProgressFillPercent)) {
    inProgressFillPercent = 0;
  }
  if (isNaN(unstartedFillPercent)) {
    unstartedFillPercent = 0;
  }

  const scalar = props.scaleFactor || styles.globalScale;
  const ringSize = props.size * scalar;

  const progressText = props.completedNum >= props.totalNum
    ? utilities.humanReadableScore(props.completedNum)
    : `${utilities.humanReadableScore(props.completedNum)} / ${utilities.humanReadableScore(props.totalNum)}`;

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.ringWrapper(ringSize),
          props.showInnerText && { // @TODO: don't piggyback off of showInnerText prop
            backgroundColor: props.backgroundColor ?? ((props.totalNum && masteredFillPercent >= 100) ? styles.colors.completeBg : styles.colors.primary),
          },
        ]}
      >
        <AnimatedRings
          size={ringSize}
          width={props.width * scalar}
          unstartedFillPercent={unstartedFillPercent}
          inProgressFillPercent={inProgressFillPercent}
          masteredFillPercent={masteredFillPercent}
          rotation={props.rotation}
          prefill={props.prefill}
          color={props.color}
        >
          {props.children
            ? () => props.children
            : props.showInnerText
              ? () => (
                <View style={styles.progressTextWrapper(ringSize)}>
                  <Text style={[styles.progressText, props.completedNum === props.totalNum && styles.peanutsProgressText]} adjustsFontSizeToFit={true} numberOfLines={progressText.length >= 8 ? 2 : 1}>
                    {progressText}
                  </Text>
                </View>
              )
              : null
          }
        </AnimatedRings>
        <Badge
          visible={props.hasBadge}
          top={styles.globalScale <= 1 ? -4 : -8}
          right={styles.globalScale <= 1 ? -4 : -8}
          size={styles.globalScale + 10}
        />
        {props.hasBadge &&
          <View style={styles.spinInnerWrapper}>
            <View style={styles.spinInner} />
          </View>
        }
      </View>
      {props.category &&
        <Text
          style={styles.progressCaption}
          numberOfLines={1}
        >
          {props.category}
        </Text>
      }
    </View>
  );
};

class ProgressIndicatorProps {
  completedNum: number;
  inProgressNum: number;
  totalNum: number;

  backgroundColor?: string;
  category?: string;
  children?: React.ReactNode;
  color?: string;
  prefill?: number;
  showInnerText?: boolean;
  size?: number = 64;
  hasBadge?: boolean;
  unstartedNum?: number;
  width?: number = 5;
  rotation?: number = 0;
  scaleFactor?: number;
};
const defaultProps = new ProgressIndicatorProps();

export default ProgressIndicator;
